import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetChargingProfileLimitMutation } from '../../../hooks/useDashboardQueries';

const MIN_AMPS = 6;
const MAX_AMPS = 32;

type ButtonStatus = 'idle' | 'pending' | 'success' | 'error';

const BUTTON_CLASS: Record<ButtonStatus, string> = {
  idle: 'limit-apply-btn',
  pending: 'limit-apply-btn limit-apply-btn--loading',
  success: 'limit-apply-btn limit-apply-btn--success',
  error: 'limit-apply-btn limit-apply-btn--error',
};

interface CurrentLimitControlProps {
  stationIdTag: string;
  connectorId: number;
  initialAmps?: number;
}

/**
 * OCPP charging-profile write widget: slider + apply button with a
 * transient success/error state on the button itself.
 */
export function CurrentLimitControl({
  stationIdTag,
  connectorId,
  initialAmps,
}: CurrentLimitControlProps) {
  const { t } = useTranslation();
  const [sliderValue, setSliderValue] = useState(
    initialAmps != null ? Math.round(initialAmps) : MAX_AMPS,
  );
  const [statusMsg, setStatusMsg] = useState<'idle' | 'success' | 'error'>('idle');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { mutate, isPending } = useSetChargingProfileLimitMutation();

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );

  const scheduleReset = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setStatusMsg('idle'), 2500);
  };

  const handleApply = () => {
    mutate(
      { idTag: stationIdTag, connectorId, amps: sliderValue },
      {
        onSuccess: () => { setStatusMsg('success'); scheduleReset(); },
        onError: () => { setStatusMsg('error'); scheduleReset(); },
      },
    );
  };

  const buttonStatus: ButtonStatus = isPending ? 'pending' : statusMsg;
  const buttonLabel: Record<ButtonStatus, string> = {
    idle: t('dashboard.tables.sessions.limitApply', 'Apply limit'),
    pending: t('dashboard.tables.sessions.limitApplying', 'Applying…'),
    success: t('dashboard.tables.sessions.limitApplied', 'Applied ✓'),
    error: t('dashboard.tables.sessions.limitFailed', 'Failed — retry'),
  };

  const trackPct = ((sliderValue - MIN_AMPS) / (MAX_AMPS - MIN_AMPS)) * 100;

  return (
    <section className="session-metrics-card session-limit-card">
      <h3 className="session-metrics-card-title">
        {t('dashboard.tables.sessions.limitTitle', 'Current Limit')}{' '}
        <span className="session-metrics-unit">(A)</span>
      </h3>

      <div className="limit-control">
        <div className="limit-slider-header">
          <span className="limit-slider-label">
            {t('dashboard.tables.sessions.limitLabel', 'Max charging current')}
          </span>
          <span className="limit-slider-value">{sliderValue} A</span>
        </div>

        <div className="limit-slider-track-wrap">
          <div className="limit-slider-fill" style={{ width: `${trackPct}%` }} />
          <input
            type="range"
            min={MIN_AMPS}
            max={MAX_AMPS}
            step={1}
            value={sliderValue}
            onChange={(e) => setSliderValue(Number(e.target.value))}
            className="limit-range-input"
            aria-label={t('dashboard.tables.sessions.limitAria', 'Charging current limit in amperes')}
          />
        </div>

        <div className="limit-range-labels">
          <span>{MIN_AMPS} A</span>
          <span>{MAX_AMPS} A</span>
        </div>

        <button
          type="button"
          className={BUTTON_CLASS[buttonStatus]}
          onClick={handleApply}
          disabled={isPending}
        >
          {buttonLabel[buttonStatus]}
        </button>
      </div>
    </section>
  );
}

export default CurrentLimitControl;
